/**
 * AI Service Module for Job Posting Generation
 * 
 * This module provides AI-powered job posting generation using Google Gemini API.
 * Features:
 * - Generate complete job postings from brief descriptions
 * - Intelligent content suggestions based on company context
 * - Multi-language support ready
 * - Usage tracking and logging
 * - Error handling and fallback mechanisms
 * 
 * AI CREDIT ALLOCATION BY SUBSCRIPTION TIER:
 * - Free Tier: 2 credits/month (2 jobs)
 * - Pro Tier: 10 credits/month (10 jobs)
 * - Premium Tier: 20 credits/month (20 jobs)
 * - Agency Tier: 50 credits/month (50 jobs)
 * Each job posting generation costs 1 credit.
 * 
 * Environment Variables Required:
 * - VITE_AI_GENERATION_ENABLED: Enable/disable feature
 *
 * NOTE: The Gemini API key is stored as a Supabase Edge Function secret (GEMINI_API_KEY)
 * and is never exposed to the frontend.
 */

import supabase from './supabase';

/**
 * Strip markdown code blocks from response
 * @private
 */
const stripMarkdownCodeBlocks = (text) => {
  // Remove markdown code fences (```json ... ``` or ``` ... ```)
  let cleaned = text.trim();
  
  // Match and remove opening code fence with optional language identifier
  cleaned = cleaned.replace(/^```(?:json|javascript|js)?\s*\n?/i, '');
  
  // Remove closing code fence
  cleaned = cleaned.replace(/\n?```\s*$/, '');
  
  return cleaned.trim();
};

/**
 * Sanitize JSON strings by escaping special characters within string values
 * This handles cases where Gemini returns JSON with unescaped newlines/tabs in strings
 * @private
 */
const sanitizeJSONStrings = (jsonStr) => {
  let result = '';
  let inString = false;
  let escaped = false;
  
  for (let i = 0; i < jsonStr.length; i++) {
    const char = jsonStr[i];
    
    if (escaped) {
      result += char;
      escaped = false;
      continue;
    }
    
    if (char === '\\') {
      result += char;
      escaped = true;
      continue;
    }
    
    if (char === '"' && !escaped) {
      inString = !inString;
      result += char;
      continue;
    }
    
    // If we're inside a string, escape special characters
    if (inString) {
      if (char === '\n') {
        result += '\\n';
      } else if (char === '\r') {
        result += '\\r';
      } else if (char === '\t') {
        result += '\\t';
      } else {
        result += char;
      }
    } else {
      result += char;
    }
  }
  
  return result;
};

/**
 * Repair common JSON issues from AI responses
 * @private
 */
const repairJSON = (jsonStr) => {
  let repaired = jsonStr;
  
  // Multiple passes for trailing comma removal to handle nested structures
  // This needs to happen before any other replacements
  for (let i = 0; i < 5; i++) {
    // Remove trailing commas before closing brackets/braces
    repaired = repaired.replace(/,(\s*[\]}])/g, '$1');
    // Also handle spaces between comma and bracket
    repaired = repaired.replace(/,\s+([\]}])/g, '$1');
  }
  
  // Fix common non-JSON values
  repaired = repaired.replace(/:\s*undefined/g, ': null');
  repaired = repaired.replace(/:\s*NaN/g, ': null');
  repaired = repaired.replace(/:\s*Infinity/g, ': 0');
  
  // Fix boolean string representations that aren't quoted
  repaired = repaired.replace(/:\s*true\b/g, ': true');
  repaired = repaired.replace(/:\s*false\b/g, ': false');
  
  // Fix single quotes to double quotes for JSON array/object values
  repaired = repaired.replace(/:\s*'([^']*)'/g, ': "$1"');
  
  // Fix common issues with description and text fields containing quotes
  // Replace smart quotes with regular quotes
  repaired = repaired.replace(/[\u201C\u201D]/g, '"');  // " " to "
  repaired = repaired.replace(/[\u2018\u2019]/g, "'");  // ' ' to '
  
  // Fix unescaped newlines in strings
  repaired = repaired.replace(/:\s*"([^"]*)\n([^"]*)"/g, ': "$1 $2"');
  
  // Fix unescaped tabs in strings
  repaired = repaired.replace(/:\s*"([^"]*)\t([^"]*)"/g, ': "$1 $2"');
  
  // Fix unescaped carriage returns
  repaired = repaired.replace(/:\s*"([^"]*)\r([^"]*)"/g, ': "$1 $2"');
  
  // Fix unescaped quotes within string values
  // This is tricky - look for patterns like ": "text "quote" text"
  // Replace internal quotes with escaped quotes
  repaired = repaired.replace(/: "([^"]*)"([^"]*)"([^"]*)"/g, ': "$1\\"$2\\"$3"');
  
  return repaired;
};

/**
 * Parse JSON with multiple fallbacks
 * Tries to extract and parse valid JSON from potentially malformed responses
 * @private
 */
const parseJSON = (jsonStr) => {
  let attempts = [];
  
  // Attempt 1: Direct parse
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    attempts.push(`Direct parse failed: ${e.message}`);
  }
  
  // Attempt 2: Repair and parse
  try {
    const repaired = repairJSON(jsonStr);
    return JSON.parse(repaired);
  } catch (e) {
    attempts.push(`Repaired parse failed: ${e.message}`);
  }
  
  // Attempt 3: Extract object properties manually
  try {
    // Try to manually construct object from response
    const obj = {};
    
    // Extract title
    const titleMatch = jsonStr.match(/"title"\s*:\s*"([^"]*(?:\\.[^"]*)*)"/);
    if (titleMatch) obj.title = titleMatch[1].replace(/\\"/g, '"').trim();
    
    // Fallback title if extraction failed
    if (!obj.title || obj.title.length === 0) {
      obj.title = 'Virtual Assistant';
    }
    
    // Extract description  
    const descMatch = jsonStr.match(/"description"\s*:\s*"((?:[^"\\]|\\.)*?)"/);
    if (descMatch) obj.description = descMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n').trim();
    
    // Fallback description if extraction failed
    if (!obj.description || obj.description.length === 0) {
      obj.description = 'We are looking for a talented Virtual Assistant to join our team. This role offers an opportunity to work with a dynamic organization and contribute to our success.';
    }
    
    // Extract responsibilities as array
    const respMatch = jsonStr.match(/"responsibilities"\s*:\s*\[([\s\S]*?)\]/);
    if (respMatch) {
      const items = respMatch[1].match(/"([^"]*(?:\\.[^"]*)*)"/g);
      obj.responsibilities = items ? items.map(s => s.slice(1, -1).replace(/\\"/g, '"').trim()).filter(s => s.length > 0) : [];
    }
    
    // Fallback responsibilities if extraction failed or insufficient items
    if (!obj.responsibilities || obj.responsibilities.length < 3) {
      obj.responsibilities = [
        'Manage calendar and schedule meetings',
        'Handle email correspondence and communication',
        'Support administrative tasks and projects',
        'Coordinate with team members',
        'Maintain organization and documentation'
      ];
    }
    
    // Extract qualifications as array
    const qualMatch = jsonStr.match(/"qualifications"\s*:\s*\[([\s\S]*?)\]/);
    if (qualMatch) {
      const items = qualMatch[1].match(/"([^"]*(?:\\.[^"]*)*)"/g);
      obj.qualifications = items ? items.map(s => s.slice(1, -1).replace(/\\"/g, '"').trim()).filter(s => s.length > 0) : [];
    }
    
    // Fallback qualifications if extraction failed or insufficient items
    if (!obj.qualifications || obj.qualifications.length < 3) {
      obj.qualifications = [
        'Proven experience as a Virtual Assistant',
        'Excellent organizational and time management skills',
        'Strong communication abilities',
        'Proficiency with productivity tools and software'
      ];
    }
    
    // Extract skills as array
    const skillsMatch = jsonStr.match(/"skills"\s*:\s*\[([\s\S]*?)\]/);
    if (skillsMatch) {
      const items = skillsMatch[1].match(/"([^"]*(?:\\.[^"]*)*)"/g);
      obj.skills = items ? items.map(s => s.slice(1, -1).replace(/\\"/g, '"').trim()).filter(s => s.length > 0) : [];
    }
    
    // Fallback skills if extraction failed or insufficient items
    if (!obj.skills || obj.skills.length < 4) {
      obj.skills = [
        'Administrative Support',
        'Email Management',
        'Calendar Coordination',
        'Project Management',
        'Communication',
        'Organization'
      ];
    }
    
    // Extract salary range - more robust matching
    const salaryMatch = jsonStr.match(/"suggestedSalaryRange"\s*:\s*{[\s\S]*?"min"\s*:\s*(\d+)[\s\S]*?"max"\s*:\s*(\d+)/);
    if (salaryMatch) {
      const min = parseInt(salaryMatch[1]);
      const max = parseInt(salaryMatch[2]);
      if (!isNaN(min) && !isNaN(max)) {
        obj.suggestedSalaryRange = {
          min: Math.min(min, max),
          max: Math.max(min, max),
          currency: "USD",
          note: "Suggested range"
        };
      }
    }
    
    // Fallback salary extraction if not in suggestedSalaryRange
    if (!obj.suggestedSalaryRange) {
      const minMatch = jsonStr.match(/"min"\s*:\s*(\d+)/);
      const maxMatch = jsonStr.match(/"max"\s*:\s*(\d+)/);
      if (minMatch && maxMatch) {
        const min = parseInt(minMatch[1]);
        const max = parseInt(maxMatch[1]);
        if (!isNaN(min) && !isNaN(max)) {
          obj.suggestedSalaryRange = {
            min: Math.min(min, max),
            max: Math.max(min, max),
            currency: "USD",
            note: "Suggested range"
          };
        }
      }
    }
    
    // Final fallback for salary range if still not found
    if (!obj.suggestedSalaryRange) {
      obj.suggestedSalaryRange = {
        min: 25,
        max: 45,
        currency: "USD",
        note: "Suggested range"
      };
    }
    
    // Extract benefits as array
    const benMatch = jsonStr.match(/"suggestedBenefits"\s*:\s*\[([\s\S]*?)\]/);
    if (benMatch) {
      const items = benMatch[1].match(/"([^"]*(?:\\.[^"]*)*)"/g);
      obj.suggestedBenefits = items ? items.map(s => s.slice(1, -1).replace(/\\"/g, '"').trim()).filter(s => s.length > 0) : [];
    }
    
    // Fallback: if no benefits extracted, try alternative field names
    if (!obj.suggestedBenefits || obj.suggestedBenefits.length === 0) {
      // Try "benefits" instead of "suggestedBenefits"
      const altBenMatch = jsonStr.match(/"benefits"\s*:\s*\[([\s\S]*?)\]/);
      if (altBenMatch) {
        const items = altBenMatch[1].match(/"([^"]*(?:\\.[^"]*)*)"/g);
        obj.suggestedBenefits = items ? items.map(s => s.slice(1, -1).replace(/\\"/g, '"').trim()).filter(s => s.length > 0) : [];
      }
    }
    
    // Final fallback: provide default benefits if extraction failed
    if (!obj.suggestedBenefits || obj.suggestedBenefits.length === 0) {
      obj.suggestedBenefits = ['Competitive Compensation', 'Professional Development', 'Flexible Work Arrangement'];
    }
    
    // Extract tone and culture
    const toneMatch = jsonStr.match(/"toneAndCulture"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    if (toneMatch) obj.toneAndCulture = toneMatch[1].replace(/\\"/g, '"').trim();
    
    // If we got meaningful data, return it
    if (Object.keys(obj).length >= 5) {
      return obj;
    }
    
    attempts.push('Manual extraction: not enough fields');
  } catch (e) {
    attempts.push(`Manual extraction failed: ${e.message}`);
  }
  
  // If all attempts failed, throw error with debug info
  throw new Error(`All JSON parsing attempts failed: ${attempts.join('; ')}`);
};

/**
 * Check if AI generation is enabled
 */
export const isAIGenerationEnabled = () => {
  return import.meta.env.VITE_AI_GENERATION_ENABLED === 'true';
};

/**
 * Generate job posting content using Google Gemini API
 * @param {Object} briefData - User's job brief
 * @param {string} briefData.title - Job title
 * @param {string} briefData.experienceLevel - Entry/Intermediate/Senior/Expert
 * @param {string} briefData.jobType - full_time/part_time/contract/freelance
 * @param {string} briefData.location - Job location
 * @param {boolean} briefData.isRemote - Is remote friendly
 * @param {Array<string>} briefData.skills - Key skills needed
 * @param {string} briefData.companyContext - Company description
 * @param {string} briefData.specialRequirements - Special requirements
 * @returns {Promise<Object>} Generated job content or error
 */
export const generateJobPosting = async (briefData) => {
  try {
    if (!isAIGenerationEnabled()) {
      throw new Error('AI generation is not enabled. Check your environment configuration.');
    }

    const { data, error } = await supabase.functions.invoke('generate-job-post', {
      body: { action: 'generate', briefData }
    });

    if (error) {
      console.error('[AIService] Edge Function error:', error);
      throw new Error(error?.message || 'Failed to generate job posting');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Edge Function returned an unsuccessful response');
    }

    // Log the generation for analytics (non-blocking)
    logAIGeneration(briefData, data.data, 0)
      .catch(err => console.error('Failed to log AI generation:', err));

    return {
      success: true,
      data: data.data,
      tokensUsed: 0,
      model: data.model || 'gemini'
    };
  } catch (error) {
    console.error('[AIService] Generation failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Refine existing job posting with user feedback
 * @param {Object} currentContent - Current job posting content
 * @param {string} refinementRequest - User's refinement request
 * @returns {Promise<Object>} Refined job content
 */
export const refineJobPosting = async (currentContent, refinementRequest) => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-job-post', {
      body: { action: 'refine', currentContent, refinementRequest }
    });

    if (error) {
      console.error('[AIService] Edge Function error:', error);
      throw new Error(error?.message || 'Failed to refine job posting');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Edge Function returned an unsuccessful response');
    }

    return {
      success: true,
      data: data.data,
      tokensUsed: 0
    };
  } catch (error) {
    console.error('[AIService] Refinement failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Validate generated content structure and completeness
 * @private
 */
const validateGeneratedContent = (content) => {
  const required = [
    'title',
    'description',
    'responsibilities',
    'qualifications',
    'skills',
    'suggestedSalaryRange',
    'suggestedBenefits'
  ];

  for (const field of required) {
    if (!content[field]) {
      return { valid: false, error: `Missing required field: ${field}` };
    }
  }

  if (!Array.isArray(content.responsibilities) || content.responsibilities.length < 3) {
    return { valid: false, error: 'Responsibilities must have at least 3 items' };
  }

  if (!Array.isArray(content.qualifications) || content.qualifications.length < 3) {
    return { valid: false, error: 'Qualifications must have at least 3 items' };
  }

  if (!Array.isArray(content.skills) || content.skills.length < 4) {
    return { valid: false, error: 'Skills must have at least 4 items' };
  }

  if (!Array.isArray(content.suggestedBenefits) || content.suggestedBenefits.length < 2) {
    return { valid: false, error: 'Benefits must have at least 2 items' };
  }

  if (!content.suggestedSalaryRange.min || !content.suggestedSalaryRange.max) {
    return { valid: false, error: 'Salary range must include min and max' };
  }

  if (content.suggestedSalaryRange.min >= content.suggestedSalaryRange.max) {
    return { valid: false, error: 'Minimum salary must be less than maximum salary' };
  }

  return { valid: true };
};

/**
 * Log AI generation for analytics and usage tracking
 * @private
 */
const logAIGeneration = async (briefData, generatedContent, tokensUsed) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.warn('[AIService] No authenticated user for logging');
      return;
    }

    const { error } = await supabase
      .from('ai_generation_logs')
      .insert({
        user_id: user.id,
        brief_data: briefData,
        generated_content: generatedContent,
        tokens_used: tokensUsed,
        generation_type: 'job_posting',
        status: 'success',
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error('[AIService] Failed to log generation:', error);
    }
  } catch (error) {
    console.error('[AIService] Error during logging:', error);
  }
};

/**
 * Check user's AI generation credits
 */
export const checkAICredits = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('ai_credits_remaining, subscription_tier')
      .eq('id', userId)
      .single();

    if (error) throw error;

    return {
      success: true,
      creditsRemaining: data.ai_credits_remaining || 0,
      hasCredits: (data.ai_credits_remaining || 0) > 0,
      subscriptionTier: data.subscription_tier || 'free'
    };
  } catch (error) {
    console.error('[AIService] Credit check failed:', error);
    return {
      success: false,
      creditsRemaining: 0,
      hasCredits: false,
      error: error.message
    };
  }
};

/**
 * Deduct AI credits from user account
 */
export const deductAICredits = async (userId, amount = 1) => {
  try {
    const { data, error } = await supabase
      .rpc('deduct_ai_credits', {
        p_user_id: userId,
        p_amount: amount
      });

    if (error) throw error;

    const result = Array.isArray(data) ? data[0] : data;

    if (!result.success) {
      throw new Error(result.error_message || 'Failed to deduct credits');
    }

    return {
      success: true,
      creditsRemaining: result.credits_remaining
    };
  } catch (error) {
    console.error('[AIService] Failed to deduct credits:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if user has exceeded rate limit for AI generation
 */
export const isGenerationAllowed = async (userId) => {
  try {
    const oneHourAgo = new Date(Date.now() - 3600000).toISOString();

    const { count, error } = await supabase
      .from('ai_generation_logs')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', oneHourAgo);

    if (error) throw error;

    const generationsThisHour = count || 0;
    const maxPerHour = 10;

    return {
      allowed: generationsThisHour < maxPerHour,
      generationsThisHour,
      maxPerHour,
      timeUntilReset: new Date(Date.now() + 3600000).toISOString()
    };
  } catch (error) {
    console.error('[AIService] Rate limit check failed:', error);
    return {
      allowed: true,
      error: error.message
    };
  }
};

/**
 * Get AI generation history for user
 */
export const getGenerationHistory = async (userId, limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('ai_generation_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('[AIService] Failed to fetch history:', error);
    return {
      success: false,
      data: [],
      error: error.message
    };
  }
};

/**
 * Test Gemini API connection via Edge Function
 * @returns {Promise<Object>} Diagnostic information
 */
export const testGeminiAPI = async () => {
  console.log('[AIService] Running Gemini API diagnostic test via Edge Function...');

  try {
    const { data, error } = await supabase.functions.invoke('generate-job-post', {
      body: {
        action: 'generate',
        briefData: {
          title: 'Test VA',
          experienceLevel: 'Entry',
          jobType: 'part_time',
          location: 'Remote',
          isRemote: true,
          skills: ['Communication'],
          companyContext: 'Diagnostic test',
          specialRequirements: 'None'
        }
      }
    });

    return {
      success: !error && data?.success,
      error: error?.message || data?.error,
      model: data?.model || 'unknown',
      hasApiKey: true // key is server-side; success means it was configured
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

// =====================================================
// AI Interview Functions
// =====================================================

/**
 * Generate a personalized AI interview question for a VA
 * @param {Object} vaProfile - VA profile (full_name, bio, skills, experience_years, etc.)
 * @param {Object|null} jobContext - { id, title, required_skills } or null for self-initiated
 * @param {Array} conversationHistory - Current conversation log entries
 * @param {number} round - Current round number (1–5)
 * @returns {Promise<Object>} { question, questionType, round, focusArea }
 */
export const generateInterviewQuestion = async (vaProfile, jobContext = null, conversationHistory = [], round = 1) => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-interview-question', {
      body: { vaProfile, jobContext, conversationHistory, round }
    });

    if (error) {
      console.error('[AIService] generate-interview-question edge error:', error);
      throw new Error(error?.message || 'Failed to generate interview question');
    }

    if (!data?.question) {
      console.error('[AIService] Invalid response from generate-interview-question:', data);
      throw new Error('Invalid question response from AI service');
    }

    return data;
  } catch (error) {
    console.error('[AIService] generateInterviewQuestion failed:', error);
    throw new Error('Failed to generate interview question. Please try again.');
  }
};

// =====================================================
// AI Skills Assessment Functions
// =====================================================

function getOrdinalSuffix(num) {
  const j = num % 10, k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

/**
 * Generate an AI-powered assessment question for a skill
 */
export const generateAssessmentQuestion = async (skillName, conversationHistory = [], targetDifficulty = 'intermediate') => {
  try {
    const { data, error } = await supabase.functions.invoke('generate-assessment-question', {
      body: { skillName, conversationHistory, targetDifficulty }
    });

    if (error) {
      console.error('[AIService] Edge Function error:', error);
      throw new Error(error?.message || 'Failed to generate assessment question');
    }

    if (!data || !data.question) {
      console.error('[AIService] Invalid question data from Edge Function:', data);
      throw new Error('Response missing required "question" field');
    }

    return data;
  } catch (error) {
    console.error('Failed to generate assessment question:', error);
    throw new Error('Failed to generate assessment question. Please try again.');
  }
};

/**
 * Evaluate user's response to an assessment question
 */
export const evaluateAssessmentResponse = async (question, userResponse, skillName) => {
  try {
    const { data, error } = await supabase.functions.invoke('evaluate-assessment-response', {
      body: { question, userResponse, skillName, conversationHistory: [] }
    });

    if (error) {
      console.error('[AIService] Edge Function error:', error);
      throw new Error(error?.message || 'Failed to evaluate response');
    }

    if (!data || typeof data.score !== 'number') {
      console.error('[AIService] Invalid evaluation data from Edge Function:', data);
      throw new Error('Invalid evaluation result structure');
    }

    return data;
  } catch (error) {
    console.error('Failed to evaluate assessment response:', error);
    throw new Error('Failed to evaluate response. Please try again.');
  }
};

/**
 * Generate comprehensive feedback for a completed skills assessment
 * @param {Array} conversationLog - Full list of Q&A rounds from the assessment
 * @param {number} finalScore - Aggregate score (0–100)
 * @param {string} skillName - Name of the skill being assessed
 * @returns {Promise<Object>} Structured feedback object
 */
export const generateAssessmentFeedback = async (conversationLog, finalScore, skillName) => {
  try {
    // Derive proficiency label from score
    let proficiencyLevel = 'Beginner';
    if (finalScore >= 80) proficiencyLevel = 'Expert';
    else if (finalScore >= 60) proficiencyLevel = 'Advanced';
    else if (finalScore >= 31) proficiencyLevel = 'Intermediate';

    const passed = finalScore >= 70;
    const roundCount = Array.isArray(conversationLog) ? conversationLog.length : 0;

    // Collect per-round scores for trend analysis
    const scores = conversationLog
      .map(round => round?.score ?? round?.evaluation?.score)
      .filter(s => typeof s === 'number');

    const avgScore = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : finalScore;

    const strengths = [];
    const improvements = [];

    if (finalScore >= 80) {
      strengths.push(`Demonstrated strong proficiency in ${skillName}`);
      strengths.push('Provided detailed and accurate answers throughout the assessment');
    } else if (finalScore >= 60) {
      strengths.push(`Showed solid foundational knowledge of ${skillName}`);
      strengths.push('Answered the majority of questions correctly');
      improvements.push('Continue practicing advanced use-cases to reach Expert level');
    } else if (finalScore >= 31) {
      strengths.push(`Demonstrated basic understanding of ${skillName}`);
      improvements.push('Focus on core concepts and practical application');
      improvements.push('Review common scenarios and best practices');
    } else {
      improvements.push(`Invest more time learning the fundamentals of ${skillName}`);
      improvements.push('Consider structured courses or hands-on practice before retaking');
    }

    return {
      summary: `Completed ${roundCount} round${roundCount !== 1 ? 's' : ''} of assessment for ${skillName} with a final score of ${finalScore}/100.`,
      proficiencyLevel,
      finalScore,
      averageRoundScore: avgScore,
      passed,
      strengths,
      areasForImprovement: improvements,
      recommendation: passed
        ? `${skillName} has been verified at ${proficiencyLevel} level. Keep practising to maintain your skills.`
        : `Score of ${finalScore} did not meet the passing threshold. Review the feedback above and retake when ready.`,
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    console.error('[AIService] Failed to generate assessment feedback:', error);
    // Return a minimal safe fallback so the assessment session can still be saved
    return {
      summary: `Assessment for ${skillName} completed with score ${finalScore}/100.`,
      finalScore,
      passed: finalScore >= 70,
      generatedAt: new Date().toISOString(),
    };
  }
};
